import { lazy, Suspense, Component } from 'react'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'

import ProtectedRoute from './components/ProtectedRoute'
import FeatureRoute   from './components/FeatureRoute'
import Layout         from './components/Layout'
import LoadingScreen  from './components/effects/LoadingScreen'
import CookieConsent  from './components/CookieConsent'

// Global error boundary — catches React render crashes
class ErrorBoundary extends Component {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(error, info) { console.error('[ALRTZ] React crash:', error, info) }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-app flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h1 className="font-display text-sm text-brand-light mb-4 glow-text">SYSTEM ERROR</h1>
            <p className="text-white text-sm mb-6">Something went wrong. Please refresh the page.</p>
            <button onClick={() => window.location.reload()} className="bg-brand text-white font-bold px-6 py-3 text-sm btn-glow">
              Refresh Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Lazy-loaded routes
const Landing      = lazy(() => import('./pages/Landing'))
const Login        = lazy(() => import('./pages/Login'))
const Register     = lazy(() => import('./pages/Register'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const Pricing      = lazy(() => import('./pages/Pricing'))
const Terms        = lazy(() => import('./pages/Terms'))
const Privacy      = lazy(() => import('./pages/Privacy'))
const Contact         = lazy(() => import('./pages/Contact'))
const ForgotPassword  = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword   = lazy(() => import('./pages/ResetPassword'))
const Onboarding   = lazy(() => import('./components/Onboarding'))
const Dashboard    = lazy(() => import('./pages/Dashboard'))
const WatchlistPg  = lazy(() => import('./pages/Watchlist'))
const CreateAlert  = lazy(() => import('./pages/CreateAlert'))
const History      = lazy(() => import('./pages/History'))
const Signals      = lazy(() => import('./pages/Signals'))
const Settings     = lazy(() => import('./pages/Settings'))
const Admin        = lazy(() => import('./pages/Admin'))
const JoinGroup    = lazy(() => import('./pages/JoinGroup'))

// AlarmOverlay — lazy since it's rarely shown
const AlarmOverlay = lazy(() => import('./components/AlarmOverlay'))

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <LoadingScreen />
      <Suspense fallback={null}>
        <AlarmOverlay />
      </Suspense>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3500,
          style: {
            background: '#080808',
            color:      '#ffffff',
            border:     '1px solid rgba(192,132,252,0.3)',
            borderRadius: '0px',
            fontSize:   '13px',
            fontFamily: '"JetBrains Mono", monospace',
            boxShadow:  '0 8px 32px rgba(0,0,0,0.8), 0 0 20px rgba(192,132,252,0.15)',
          },
          success: { iconTheme: { primary: '#22c55e', secondary: '#080808' } },
          error:   { iconTheme: { primary: '#ef4444', secondary: '#080808' } },
        }}
      />

      {/* CRT / cyberpunk global overlays */}
      <div className="scanlines" />
      <div className="noise" />
      <div className="vignette" />

      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/"         element={<Landing />} />
          <Route path="/login"    element={<Login />} />
          <Route path="/register"      element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/pricing"       element={<Pricing />} />
          <Route path="/join/:code"    element={<Suspense fallback={<RouteFallback />}><JoinGroup /></Suspense>} />
          <Route path="/terms"         element={<Terms />} />
          <Route path="/privacy"       element={<Privacy />} />
          <Route path="/contact"          element={<Contact />} />
          <Route path="/forgot-password"  element={<ForgotPassword />} />
          <Route path="/reset-password"   element={<ResetPassword />} />

          {/* Onboarding (protected, no sidebar layout) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          {/* Protected app routes — wrapped in sidebar Layout */}
          <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/watchlist" element={<ProtectedRoute><Layout><WatchlistPg /></Layout></ProtectedRoute>} />
          <Route path="/create"    element={<ProtectedRoute><Layout><CreateAlert /></Layout></ProtectedRoute>} />
          <Route path="/history"   element={<ProtectedRoute><Layout><History /></Layout></ProtectedRoute>} />
          <Route path="/signals"  element={<ProtectedRoute><FeatureRoute flag="signals"><Layout><Signals /></Layout></FeatureRoute></ProtectedRoute>} />
          <Route path="/settings"  element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
          <Route path="/admin"     element={<ProtectedRoute><FeatureRoute flag="admin_panel"><Layout><Admin /></Layout></FeatureRoute></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
    <Analytics />
    <SpeedInsights />
    <CookieConsent />
    </ErrorBoundary>
  )
}

export default App
